/**
 * How each Availability cell state is drawn. Every colour is a product token
 * (`--avail-*` in design-system/tokens.css), so a state re-themes in one place.
 * The number printed in the cell carries the meaning; colour only reinforces it.
 */
import { type CellResult, type CellState, displayValue } from "@/lib/availability/cell-state";
import { formatHours, STATE_LABEL } from "./labels";

/** The fill, text colour and edge of a grid cell for each state. */
export const CELL_CLASS: Record<CellState, string> = {
  overUtilized: "bg-[var(--avail-over)] text-[color:var(--avail-over-fg)] font-semibold",
  stretched: "bg-[var(--avail-stretched)] text-[color:var(--avail-stretched-fg)] font-medium",
  fullyBooked:
    "bg-card text-muted-foreground font-medium outline outline-1 -outline-offset-1 outline-[color:var(--avail-fully-booked-border)]",
  someRoom: "bg-[var(--avail-some-room)] text-[color:var(--avail-some-room-fg)] font-medium",
  available: "bg-[var(--avail-available)] text-[color:var(--avail-available-fg)] font-semibold",
  unplanned:
    "bg-card text-muted-foreground font-medium outline outline-1 -outline-offset-1 outline-dashed outline-[color:var(--avail-unplanned-border)]",
  noData: "bg-[var(--avail-nodata)] text-[color:var(--avail-nodata-fg)] font-medium",
};

/** The state pill in a panel. Fully booked and Unplanned read as neutral chips. */
export const CHIP_CLASS: Record<CellState, string> = {
  overUtilized: "bg-[var(--avail-over)] text-[color:var(--avail-over-fg)]",
  stretched: "bg-[var(--avail-stretched)] text-[color:var(--avail-stretched-fg)]",
  fullyBooked: "bg-muted text-heading",
  someRoom: "bg-[var(--avail-some-room)] text-[color:var(--avail-some-room-fg)]",
  available: "bg-[var(--avail-available)] text-[color:var(--avail-available-fg)]",
  unplanned: "bg-muted text-heading",
  noData: "bg-[var(--avail-nodata)] text-[color:var(--avail-nodata-fg)]",
};

/** Mini-bar fills (Last 3 mo, the 6-month view). Built from the same tokens. */
export const BAR_CLASS: Record<CellState, string> = {
  overUtilized: "bg-[var(--avail-over)]",
  stretched: "bg-[var(--avail-stretched-fg)]",
  fullyBooked: "bg-[var(--avail-unplanned-border)]",
  someRoom: "bg-[color-mix(in_oklab,var(--avail-free-bar)_55%,transparent)]",
  available: "bg-[var(--avail-free-bar)]",
  unplanned: "bg-[var(--avail-fully-booked-border)]",
  noData: "bg-[var(--avail-fully-booked-border)]",
};

/** What is printed inside a cell: `+42`, `28`, `–` for No data, or nothing. */
export function cellText(result: CellResult): string {
  if (result.state === "noData") return "–";
  // displayValue gives "+1140"; group roll-ups run to four digits, so group them: "+1,140".
  return (displayValue(result) ?? "").replace(/\d+/, (digits) => formatHours(Number(digits)));
}

/** Screen-reader text for a cell button: person, month, state, and the hours. */
export function cellAriaLabel(
  name: string,
  monthLabel: string,
  result: CellResult,
  suffix = "",
): string {
  const hours = Math.round(Math.abs(result.netHours));
  let detail = "";
  if (result.state === "overUtilized" || result.state === "stretched") {
    detail = `, ${hours} hours over`;
  } else if (result.state === "someRoom" || result.state === "available") {
    detail = `, ${hours} hours free`;
  }
  return `${name}, ${monthLabel}, ${STATE_LABEL[result.state]}${detail}${suffix}`;
}
